import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, FlexColumn, Icon, Popover, Spinner, Text } from 'core/components';
import { MCP_AUTH_TYPE } from '@kentik/ui-shared/kaizen/constants';
import { MCP_BUILTIN_SERVERS } from '@kentik/ui-shared/kaizen/mcpBuiltinServers';
import McpOAuthControls from 'app/components/McpOAuthControls';
import { canAccessBuiltinIntegrations } from 'app/views/settings/ai/kentikAiSettingsTabs';

const BUILTIN_SERVERS = Object.values(MCP_BUILTIN_SERVERS);

@inject('$mcpServers', '$builtinMcpServers', '$auth')
@observer
class McpStatusIndicator extends Component {
  get hasAccess() {
    const { $auth } = this.props;

    return (
      $auth.hasPermission('aiMcpServers.enabled', { overrideForSudo: false }) &&
      $auth.hasRbacPermissions(['ai.mcp_servers::read'])
    );
  }

  // Built-in servers need neither the MCP Servers feature nor its RBAC.
  get hasBuiltinAccess() {
    const { $auth } = this.props;
    return canAccessBuiltinIntegrations($auth);
  }

  componentDidMount() {
    const { $mcpServers, $builtinMcpServers } = this.props;

    if (this.hasBuiltinAccess) {
      BUILTIN_SERVERS.forEach(({ key }) => $builtinMcpServers.load(key).catch(() => null));
    }

    if (!this.hasAccess) {
      return;
    }

    $mcpServers.loadServersWithStatus();
  }

  get builtinServers() {
    const { $builtinMcpServers } = this.props;

    if (!this.hasBuiltinAccess) {
      return [];
    }

    return BUILTIN_SERVERS.flatMap(({ key, displayName }) => {
      const state = $builtinMcpServers.getState(key);

      if (!state?.enabled) {
        return [];
      }

      const connected = state.connected || !!$builtinMcpServers.getConnection(key).result?.connected;

      return [
        connected
          ? { id: key, builtinKey: key, name: displayName, tone: 'success', detail: 'Connected' }
          : { id: key, builtinKey: key, name: displayName, tone: 'warning', detail: 'Not connected', canConnect: true }
      ];
    });
  }

  describe(model) {
    const { $mcpServers } = this.props;
    const id = model.get('id');

    if (!model.get('enabled')) {
      return { id, name: model.get('name'), tone: 'muted', detail: 'Disabled' };
    }

    const status = $mcpServers.statusMap.get(id);
    const isOAuth = model.get('auth_type') === MCP_AUTH_TYPE.OAUTH;

    if (!status) {
      return { id, name: model.get('name'), loading: true };
    }

    if (status.connected === null) {
      return { id, name: model.get('name'), tone: 'muted', detail: 'Unknown' };
    }

    if (!status.connected) {
      return { id, name: model.get('name'), tone: 'danger', detail: 'Unreachable' };
    }

    if (status.authenticated === false) {
      return {
        id,
        name: model.get('name'),
        tone: 'warning',
        detail: isOAuth ? 'Not connected' : 'Credentials rejected',
        canConnect: isOAuth
      };
    }

    return { id, name: model.get('name'), tone: 'success', detail: 'Connected' };
  }

  // Custom servers connect through McpOAuthControls; built-in servers have their own routes and state.
  handleBuiltinConnect = (server) => {
    const { $builtinMcpServers } = this.props;

    $builtinMcpServers.connect(server.builtinKey);
  };

  render() {
    const { $mcpServers } = this.props;

    const models = this.hasAccess ? $mcpServers.collection.get() || [] : [];
    const customServers = models.some((model) => model.get('enabled'))
      ? models.map((model) => this.describe(model))
      : [];
    const servers = [...this.builtinServers, ...customServers];

    if (servers.length === 0) {
      return null;
    }

    const healthyCount = servers.filter((server) => server.tone === 'success').length;

    return (
      <Popover
        position="bottom-right"
        content={
          <FlexColumn p={2} gap={1} minWidth={240}>
            <Text fontWeight="bold" mb="4px">
              MCP Servers
            </Text>
            {servers.map((server) => (
              <Flex key={server.id} alignItems="center" gap={1}>
                {server.loading ? <Spinner size={10} /> : <Icon icon="full-circle" size={8} color={server.tone} />}
                <Text small>{server.name}</Text>
                {!server.loading && (
                  <Text small muted ml="4px">
                    {server.detail}
                  </Text>
                )}
                {server.canConnect && (
                  <Box ml="auto">
                    {server.builtinKey ? (
                      <Button minimal small text="Connect" onClick={() => this.handleBuiltinConnect(server)} />
                    ) : (
                      <McpOAuthControls serverId={server.id} serverName={server.name} />
                    )}
                  </Box>
                )}
              </Flex>
            ))}
          </FlexColumn>
        }
      >
        <Button minimal icon="data-connection" title="MCP server status">
          <Text>
            MCP ({healthyCount}/{servers.length})
          </Text>
        </Button>
      </Popover>
    );
  }
}

export default McpStatusIndicator;
